import React, { useState, useLayoutEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { AccordionProvider } from './accordion.context';

/**
 * @typedef Props
 * @prop {React.ReactNode} children
 * @prop  {string} [className]
 * @prop {'functional' | 'functional-alt' | 'editorial'} [variant]
 * @prop {() => void} [onOpen]
 * @prop {() => void} [onClose]
 * @prop {boolean} [open]
 * @prop {boolean} [useChevronIcon]
 * @prop {boolean} [alignIconRight]
 */
/**
 * @param {Props & {[x: string]: any;}} props
 */
const MotifAccordion = (props) => {
  const {
    children,
    className = '',
    variant = 'functional',
    onOpen = null,
    onClose = null,
    open = null,
    useChevronIcon = false,
    alignIconRight = false,
    ...other
  } = props;
  const [localVisible, setLocalVisible] = useState(false);
  const firstUpdate = useRef(true);
  const isControlled = open !== null;
  const visible = isControlled ? open : localVisible;

  useLayoutEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;

      return;
    }

    if (isControlled) {
      if (open) {
        onOpen();
      } else {
        onClose();
      }
    }
  });

  const toggleAccordion = () => {
    if (!visible && onOpen) {
      onOpen();
    } else if (visible && onClose) {
      onClose();
    }
    if (!isControlled) {
      setLocalVisible(!visible);
    }
  };

  const classes = classNames({
    'motif-accordion': true,
    'motif-accordion-content-visible': visible,
    'motif-accordion-trigger-align-right': alignIconRight,
    [`motif-accordion-${variant}`]: true,
    [className]: true
  });

  const contextConfig = {
    visible,
    toggleAccordion,
    alignIconRight,
    useChevronIcon
  };

  return (
    <AccordionProvider value={contextConfig}>
      <div className={classes} {...other} data-motif-accordion>
        {children}
      </div>
    </AccordionProvider>
  );
};

MotifAccordion.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  variant: PropTypes.oneOf(['functional', 'functional-alt', 'editorial']),
  open: PropTypes.bool,
  alignIconRight: PropTypes.bool,
  useChevronIcon: PropTypes.bool
};

export { MotifAccordion };
